import styled from 'styled-components/macro'
import Meter from 'svg/meter.svg?react'
import { Text } from 'roadie-ui'

// ─── Indicator geometry ───────────────────────────────────────────────────────
// The meter SVG has three fixed visual bands whose boundaries correspond to
// specific angles on the arc. We map a score's band (red/yellow/green) onto
// those visual angles, using the caller-supplied thresholds to define where
// each band starts.
//
// Fixed visual angle boundaries (from meter.svg):
//   180° → leftmost point  (start of red)
//    56.3° → red/yellow boundary
//    28.3° → yellow/green boundary
//     1° → rightmost point (end of green)

const TRACK_CX = 161
const TRACK_CY = 162
const TRACK_R = 142

function getIndicatorAngleDeg(score: number, yellowMin: number, greenMin: number): number {
  const s = Math.max(0, Math.min(100, score))
  // Use midpoints between integer thresholds so the dot sits inside the band
  const redMax = yellowMin - 0.5
  const yellowMax = greenMin - 0.5
  if (s <= redMax) {
    return 180 - (s / redMax) * (180 - 56.3)
  } else if (s <= yellowMax) {
    return 56.3 - ((s - redMax) / (yellowMax - redMax)) * (56.3 - 28.3)
  } else {
    return 28.3 - ((s - yellowMax) / (100 - yellowMax)) * (28.3 - 1)
  }
}

function getIndicatorPos(score: number, yellowMin: number, greenMin: number) {
  const rad = (getIndicatorAngleDeg(score, yellowMin, greenMin) * Math.PI) / 180
  return { x: TRACK_CX + TRACK_R * Math.cos(rad), y: TRACK_CY - TRACK_R * Math.sin(rad) }
}

function getDotColor(score: number, yellowMin: number, greenMin: number): string {
  if (score >= greenMin) return '#72BF44'
  if (score >= yellowMin) return '#FDB913'
  return '#E34025'
}

const MeterBase = styled(Meter)`
  display: block;
  width: 100%;
  height: auto;

  & > g > g:last-child {
    display: none;
  }
`

const MeterWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 380px;
  margin: 0 auto;
`

const IndicatorSvg = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
`

const ScoreOverlay = styled.div`
  position: absolute;
  bottom: 6%;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
  white-space: nowrap;
  pointer-events: none;
  display: flex;
  align-items: center;
`

type RateMeterProps = {
  score: number
  yellowMin: number
  greenMin: number
}

const RateMeter: React.FC<RateMeterProps> = ({ score, yellowMin, greenMin }) => {
  const dot = getIndicatorPos(score, yellowMin, greenMin)
  const dotColor = getDotColor(score, yellowMin, greenMin)

  return (
    <MeterWrapper>
      <MeterBase />

      <IndicatorSvg viewBox="0 0 323 172" aria-hidden="true">
        <defs>
          <filter
            id="dynamic-dot-shadow"
            x="0"
            y="0"
            width="323"
            height="179"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="7.00171" />
            <feGaussianBlur stdDeviation="3.50085" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix type="matrix" values="0 0 0 0 0.0509804 0 0 0 0 0.0392157 0 0 0 0 0.172549 0 0 0 0.08 0" />
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
          </filter>
        </defs>

        <g filter="url(#dynamic-dot-shadow)">
          <circle cx={dot.x} cy={dot.y} r="15" fill="white" />
          <circle cx={dot.x} cy={dot.y} r="11.9368" stroke={dotColor} strokeWidth="6.1265" fill="none" />
        </g>
      </IndicatorSvg>

      <ScoreOverlay>
        <Text.H5 $fontSize="9.2rem" $fontWeight="500" $color="neutral-white">
          {score}
        </Text.H5>
        <Text.H5 $fontSize="4.14rem" $fontWeight="500" $color="neutral-white">
          %
        </Text.H5>
      </ScoreOverlay>
    </MeterWrapper>
  )
}

export default RateMeter
